// Grid Logic: sudoku-lite with shapes, then digits, and little logic grids with clues.
// One attempt per puzzle: the first Check decides it, then she can keep fixing.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import type { GameModule, GameContext } from '@shell/types';
import { makeSudoku, conflicts, makeLogicGrid, sudokuSizeFor, pickPuzzleKinds, SYMBOL_COLORS, SYMBOL_SHAPES, type Sudoku, type LogicGrid } from '@problems/grid-logic';

function mount(host: HTMLElement, ctx: GameContext) {
  let level = ctx.currentLevel();
  const kinds = pickPuzzleKinds(level, ctx.rng);
  let idx = 0;
  let attempts = 0, correct = 0;
  let checkedOnce = false, hinted = false, done = false;
  const t0 = performance.now();
  let tPuzzle = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Puzzle ', h('strong', null, '1'), ` of ${kinds.length}`), h('span.muted.small', null, ''));
  const body = h('div.stack');
  host.append(head, body);
  let cleanup: () => void = () => {};

  function start() {
    cleanup(); clear(body);
    checkedOnce = false; hinted = false; done = false; tPuzzle = performance.now();
    level = ctx.currentLevel();
    (head.querySelector('strong') as HTMLElement).textContent = String(idx + 1);
    if (kinds[idx] === 'sudoku') sudokuPuzzle(); else logicPuzzle();
  }

  function score(ok: boolean, wrongCount: number) {
    const ms = performance.now() - tPuzzle;
    if (!checkedOnce) {
      checkedOnce = true; attempts++;
      if (ok) correct++;
      ctx.report({ skill: 'logic', level, correct: ok, hintUsed: hinted, ms, problemId: `grid:${kinds[idx]}:${level}` });
    }
    if (ok) {
      done = true;
      ctx.sound('chime');
      ctx.say('Solved!');
      setTimeout(next, 1100);
    } else {
      ctx.sound('buzz');
      ctx.say(`${wrongCount} cell${wrongCount === 1 ? ' is' : 's are'} not right yet. Keep going, you can fix them.`);
    }
  }

  function next() {
    idx++;
    if (idx >= kinds.length) {
      if (attempts === correct && kinds.includes('sudoku') && sudokuSizeFor(level) === 9) ctx.charm('charm.sudoku');
      ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 });
      return;
    }
    start();
  }

  // ---------------- sudoku ----------------
  function sudokuPuzzle() {
    const n = sudokuSizeFor(level);
    const p: Sudoku = makeSudoku(n, ctx.rng);
    const grid = p.givens.slice();
    let sel = -1;
    const symbols = n === 4 ? SYMBOL_SHAPES : Array.from({ length: n }, (_, i) => String(i + 1));
    (head.lastElementChild as HTMLElement).textContent = n === 4 ? 'Every row, column and box gets each shape once' : `Each row, column and box: 1 to ${n} once`;
    const stage = h('div');
    const palette = h('div.row.wrap', { style: { justifyContent: 'center', gap: '6px' } });
    const actions = h('div.grid2', null,
      h('button.btn', { onclick: () => { if (hinted || done) { ctx.say(hinted ? 'One hint per puzzle.' : ''); return; } const empties = grid.map((v, i) => (v !== p.solution[i] ? i : -1)).filter(i => i >= 0); if (!empties.length) return; const i = empties[Math.floor(ctx.rng() * empties.length)]; grid[i] = p.solution[i]; hinted = true; sel = i; ctx.sound('pop'); ctx.say('A cell filled itself in. The rest is yours.'); draw(); } }, 'Hint'),
      h('button.btn.primary', { onclick: () => { const wrong = grid.filter((v, i) => v !== p.solution[i]).length; score(wrong === 0, wrong); draw(); } }, 'Check'),
    );
    body.append(stage, palette, actions);
    const S = makeSurface(stage, { aspect: 1, onResize: () => draw() });
    const un = attachPointer(S.canvas, { tap(pt) { if (done) return; const cell = S.w / n; const c = Math.floor(pt.x / cell), r = Math.floor(pt.y / cell); if (c < 0 || r < 0 || c >= n || r >= n) return; const i = r * n + c; if (p.givens[i]) { ctx.say('That one was given.'); return; } sel = i; ctx.sound('tick'); draw(); } });
    cleanup = () => { un(); S.destroy(); };

    symbols.forEach((sym, k) => {
      const b = h('button', { onclick: () => place(k + 1), style: { width: '48px', height: '48px', borderRadius: '10px', border: '2px solid var(--line)', background: 'var(--surface)', fontWeight: '900', fontSize: '20px' } }, n === 4 ? '' : sym);
      if (n === 4) b.innerHTML = symbolSvg(k, 28);
      palette.append(b);
    });
    palette.append(h('button.btn.small', { onclick: () => place(0) }, 'Erase'));

    function place(v: number) {
      if (done) return;
      if (sel < 0) { ctx.say('Tap a cell first.'); return; }
      grid[sel] = v; ctx.sound('pop'); draw();
    }

    function draw() {
      const c = S.ctx, W = S.w;
      if (W < 16) return;
      const cell = W / n;
      const bad = conflicts(n, grid);
      c.fillStyle = '#fff'; c.fillRect(0, 0, W, W);
      for (let i = 0; i < n * n; i++) {
        const r = Math.floor(i / n), col = i % n, x = col * cell, y = r * cell;
        c.fillStyle = p.givens[i] ? '#efe9db' : '#fff';
        if (bad.has(i)) c.fillStyle = '#fbe0dc';
        if (i === sel) c.fillStyle = '#d6f1ee';
        c.fillRect(x, y, cell, cell);
        const v = grid[i];
        if (v) {
          if (n === 4) drawSymbol(c, v - 1, x + cell / 2, y + cell / 2, cell * 0.28);
          else { c.fillStyle = p.givens[i] ? '#2a2a2e' : '#256abf'; c.font = `${p.givens[i] ? 900 : 700} ${Math.round(cell * 0.5)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText(String(v), x + cell / 2, y + cell / 2 + 1); }
        }
      }
      c.strokeStyle = '#c9c4b4'; c.lineWidth = 1;
      for (let i = 0; i <= n; i++) { c.beginPath(); c.moveTo(i * cell, 0); c.lineTo(i * cell, W); c.stroke(); c.beginPath(); c.moveTo(0, i * cell); c.lineTo(W, i * cell); c.stroke(); }
      c.strokeStyle = '#2a2a2e'; c.lineWidth = 3;
      for (let i = 0; i <= n; i += p.boxCols) { c.beginPath(); c.moveTo(i * cell, 0); c.lineTo(i * cell, W); c.stroke(); }
      for (let i = 0; i <= n; i += p.boxRows) { c.beginPath(); c.moveTo(0, i * cell); c.lineTo(W, i * cell); c.stroke(); }
    }
    ctx.say(n === 4 ? 'Tap a cell, then tap a shape.' : 'Tap a cell, then tap a number.');
    draw();
  }

  function drawSymbol(c: CanvasRenderingContext2D, k: number, x: number, y: number, r: number) {
    c.fillStyle = SYMBOL_COLORS[k];
    c.beginPath();
    if (k === 0) c.arc(x, y, r, 0, 7);
    else if (k === 1) c.rect(x - r, y - r, 2 * r, 2 * r);
    else if (k === 2) { c.moveTo(x, y - r * 1.1); c.lineTo(x + r * 1.1, y + r * 0.9); c.lineTo(x - r * 1.1, y + r * 0.9); c.closePath(); }
    else { c.moveTo(x, y - r * 1.15); c.lineTo(x + r * 1.15, y); c.lineTo(x, y + r * 1.15); c.lineTo(x - r * 1.15, y); c.closePath(); }
    c.fill();
  }
  function symbolSvg(k: number, size: number): string {
    const col = SYMBOL_COLORS[k];
    const inner = k === 0 ? `<circle cx="20" cy="20" r="13" fill="${col}"/>` : k === 1 ? `<rect x="8" y="8" width="24" height="24" fill="${col}"/>` : k === 2 ? `<path d="M20 6 L34 32 L6 32Z" fill="${col}"/>` : `<path d="M20 5 L34 20 L20 35 L6 20Z" fill="${col}"/>`;
    return `<svg viewBox="0 0 40 40" width="${size}" height="${size}">${inner}</svg>`;
  }

  // ---------------- logic grid ----------------
  function logicPuzzle() {
    const catCount: 1 | 2 = level >= 7 ? 2 : 1;
    const g: LogicGrid = makeLogicGrid(ctx.rng, catCount);
    (head.lastElementChild as HTMLElement).textContent = 'Read the clues, mark O for yes and X for no';
    // marks[c][p][item] = 0 blank, 1 = O, 2 = X
    const marks = g.cats.map(() => [0, 1, 2].map(() => [0, 0, 0]));
    const clues = h('div.card', null, h('h3', null, 'Clues'), ...g.clues.map(t => h('p', { style: { margin: '4px 0' } }, t)));
    const grids = h('div.stack');
    body.append(clues, grids, h('div.grid2', null,
      h('button.btn', { onclick: () => { if (hinted || done) return; const c = 0, p = Math.floor(ctx.rng() * 3); marks[c][p] = [0, 0, 0]; marks[c][p][g.solution[c][p]] = 1; hinted = true; ctx.sound('pop'); ctx.say(`Hint: ${g.names[p]} gets ${g.cats[c].items[g.solution[c][p]]}.`); renderGrids(); } }, 'Hint'),
      h('button.btn.primary', { onclick: () => { let wrong = 0; g.cats.forEach((_, c) => { for (let p = 0; p < 3; p++) for (let i = 0; i < 3; i++) { const want = g.solution[c][p] === i ? 1 : 2; const have = marks[c][p][i]; if (have === 1 && want !== 1) wrong++; if (want === 1 && have !== 1) wrong++; } }); score(wrong === 0, wrong); } }, 'Check'),
    ));
    cleanup = () => {};
    function renderGrids() {
      clear(grids);
      g.cats.forEach((cat, c) => {
        const table = h('table', { style: { borderCollapse: 'collapse', width: '100%' } });
        const hdr = h('tr', null, h('th', null, ''), ...cat.items.map(it => h('th', { style: { fontSize: '13px', padding: '4px' } }, it)));
        table.append(hdr);
        for (let p = 0; p < 3; p++) {
          const row = h('tr', null, h('th', { style: { textAlign: 'left', fontSize: '14px', padding: '4px' } }, g.names[p]));
          for (let i = 0; i < 3; i++) {
            const m = marks[c][p][i];
            row.append(h('td', { style: { padding: '2px' } }, h('button', {
              onclick: () => { if (done) return; marks[c][p][i] = (m + 1) % 3; ctx.sound('tick'); renderGrids(); },
              style: { width: '100%', minHeight: '46px', borderRadius: '8px', border: '2px solid var(--line)', background: m === 1 ? '#d6f1ee' : m === 2 ? '#f6f3ec' : 'var(--surface)', fontWeight: '900', fontSize: '20px', color: m === 1 ? '#0f7e5c' : '#9a9aa3' },
            }, m === 1 ? 'O' : m === 2 ? 'X' : '')));
          }
          table.append(row);
        }
        grids.append(h('div.card', null, h('p.muted.small', null, `Who ${cat.label === 'pet' ? 'has which pet' : cat.label === 'color' ? 'likes which color' : cat.label === 'snack' ? 'eats which snack' : 'loves which hobby'}?`), table));
      });
    }
    renderGrids();
    ctx.say('Tap a square to cycle: blank, O for yes, X for no.');
  }

  start();
  return { destroy() { cleanup(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.grid-logic',
  title: 'Grid Logic',
  blurb: 'Sudoku with shapes, then numbers. Later, who-has-what puzzles with clues.',
  category: 'math',
  skills: ['logic'],
  levelRange: [1, 10],
  unlock: { stars: 14 },
  mount,
};
export default mod;
